import { makeAutoObservable, runInAction } from 'mobx'
import { AxiosError, type AxiosResponse } from 'axios'
import { apiOptionsToDropdownItems, logger } from 'shared/lib'
import { showToast, type IDropdownItem } from 'shared/ui'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { type INormalizedApiError } from 'shared/api'
import { uiStore } from 'shared/store/uiStore'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { RcsApi, type IResponseRcsCompliance, type IRcsComplianceStatus } from 'entities/Rcs'
import { type PresentationModeStore } from 'widgets/PresentationMode'
import { RcsPath } from 'pages/settings/pages/rcs/route/rcsPath'
import { RcsWizardSteps } from 'pages/settings/pages/rcsV2/route/type'
import { SenderDetailsStore } from 'pages/settings/pages/rcsV2/pages/create/SenderDetails/store/senderDetailsStore'
import { CountriesStore } from 'pages/settings/pages/rcsV2/pages/create/Countries/store/countriesStore'
import { BusinessInfoStore } from 'pages/settings/pages/rcsV2/pages/create/BusinessInfo/store/businessInfoStore'
import { ContactInfoStore } from 'pages/settings/pages/rcsV2/pages/create/ContactInfo/store/contactInfoStore'
import { UseCaseStore } from 'pages/settings/pages/rcsV2/pages/create/UseCase/store/useCaseStore'
import { OptInMessageInfoStore } from 'pages/settings/pages/rcsV2/pages/create/OptInMessageInfo/store/optInMessageInfoStore'
import { ReviewStore } from 'pages/settings/pages/rcsV2/pages/create/Review/store/reviewStore'
import { RcsWizardNavigationStore } from './rcsWizardNavigationStore'
import { RcsFallbackStore } from './rcsFallbackStore'

const START_OVER_MODAL_ID = 'rcsStartOverConfirm'
const SUBMITTED_MODAL_ID = 'rcsSubmittedForReview'

export class RcsWizardStore {
  private _loading = true
  private _messageTypeOptions: IDropdownItem[] = []
  private _messageTypesLoading = false
  private _companyTypeOptions: IDropdownItem[] = []
  private _businessIndustryOptions: IDropdownItem[] = []
  private _businessReferencesLoading = false
  private _useCaseOptions: IDropdownItem[] = []
  private _useCasesLoading = false
  private _submissionId: number | null = null
  private _saving = false
  private _complianceStatus: IRcsComplianceStatus | null = null
  private _lastStep: number | null = null
  private _submission: IResponseRcsCompliance | null = null
  readonly navigationStore: RcsWizardNavigationStore
  readonly senderStore: SenderDetailsStore
  readonly countriesStore: CountriesStore
  readonly businessInfoStore: BusinessInfoStore
  readonly contactInfoStore: ContactInfoStore
  readonly useCaseStore: UseCaseStore
  readonly optInMessageInfoStore: OptInMessageInfoStore
  readonly reviewStore: ReviewStore
  readonly fallbackStore: RcsFallbackStore

  constructor(
    private _entities: IEntitiesStore,
    private _presentationStore: PresentationModeStore
  ) {
    this.navigationStore = new RcsWizardNavigationStore(this)
    this.senderStore = new SenderDetailsStore(this)
    this.countriesStore = new CountriesStore()
    this.businessInfoStore = new BusinessInfoStore(this, this._entities)
    this.contactInfoStore = new ContactInfoStore(this)
    this.useCaseStore = new UseCaseStore(this, this._entities)
    this.optInMessageInfoStore = new OptInMessageInfoStore(
      this,
      this._presentationStore,
      this._entities
    )
    this.reviewStore = new ReviewStore()
    this.fallbackStore = new RcsFallbackStore(this._entities)

    makeAutoObservable(this)
    this.init()
  }

  get loading() {
    return this._loading
  }

  get messageTypeOptions() {
    return this._messageTypeOptions
  }

  get messageTypesLoading() {
    return this._messageTypesLoading
  }

  get companyTypeOptions() {
    return this._companyTypeOptions
  }

  get businessIndustryOptions() {
    return this._businessIndustryOptions
  }

  get businessReferencesLoading() {
    return this._businessReferencesLoading
  }

  get useCaseOptions() {
    return this._useCaseOptions
  }

  get useCasesLoading() {
    return this._useCasesLoading
  }

  get saving() {
    return this._saving
  }

  get submissionId() {
    return this._submissionId
  }

  get complianceStatus() {
    return this._complianceStatus
  }

  get lastStep() {
    return this._lastStep
  }

  get submission() {
    return this._submission
  }

  get isUsFlow() {
    return this.countriesStore.hasUsSelected
  }

  nextStepClick = async (step: RcsWizardSteps) => {
    const saved = await this._saveCurrentStep(step)
    if (saved) this.navigationStore.goToNextStep(step)
  }

  submit = async () => {
    const id = this._submissionId
    if (id === null) return

    runInAction(() => {
      this._saving = true
    })

    try {
      const apiStep = this.navigationStore.getApiStep(RcsWizardSteps.review)
      const { data } = await RcsApi.updateRcsComplianceStep(id, apiStep)

      runInAction(() => {
        this._applyComplianceMeta(data)
      })

      if (this.isUsFlow) {
        this._showSubmittedForReviewModal()
      } else {
        this.closeWizard()
      }
    } catch (error) {
      logger.error(error)

      if (error instanceof AxiosError) {
        this._applySubmitErrors(error.errors)
      }

      showToast({
        type: 'error',
        title: 'Failed to submit for review',
        desc: 'Please review your details and try again.',
      })
    } finally {
      runInAction(() => {
        this._saving = false
      })
    }
  }

  private _saveCurrentStep = async (step: RcsWizardSteps): Promise<boolean> => {
    runInAction(() => {
      this._saving = true
    })

    try {
      const { data } = await this._sendStep(step)

      runInAction(() => {
        this._applyComplianceMeta(data)
      })

      return true
    } catch (error) {
      logger.error(error)

      if (error instanceof AxiosError) {
        this._applyStepErrors(step, error.errors)
      }

      return false
    } finally {
      runInAction(() => {
        this._saving = false
      })
    }
  }

  private _sendStep = (step: RcsWizardSteps): Promise<AxiosResponse<IResponseRcsCompliance>> => {
    const apiStep = this.navigationStore.getApiStep(step)

    if (step === RcsWizardSteps.senderDetails) {
      return this._submissionId === null
        ? RcsApi.createRcsCompliance(this.senderStore.params)
        : RcsApi.updateRcsComplianceStep(this._submissionId, apiStep, this.senderStore.params)
    }

    const id = this._submissionId
    if (id === null) {
      return Promise.reject(new Error('RCS submission is not created yet'))
    }

    switch (step) {
      case RcsWizardSteps.countries:
        return RcsApi.updateRcsComplianceStep(id, apiStep, this.countriesStore.params)
      case RcsWizardSteps.businessInfo:
        return RcsApi.updateRcsComplianceStep(id, apiStep, this.businessInfoStore.params)
      case RcsWizardSteps.contactInfo:
        return RcsApi.updateRcsComplianceStep(id, apiStep, this.contactInfoStore.params)
      case RcsWizardSteps.useCase:
        return RcsApi.updateRcsComplianceStep(id, apiStep, this.useCaseStore.params)
      case RcsWizardSteps.optInMessageInfo:
        return RcsApi.updateRcsComplianceStep(id, apiStep, this.optInMessageInfoStore.params)
      default:
        return Promise.reject(new Error(`Step "${step}" is not wired for save`))
    }
  }

  private _applyStepErrors = (step: RcsWizardSteps, errors: INormalizedApiError) => {
    switch (step) {
      case RcsWizardSteps.senderDetails:
        this.senderStore.initErrors(errors)
        break
      case RcsWizardSteps.businessInfo:
        this.businessInfoStore.initErrors(errors)
        break
      case RcsWizardSteps.contactInfo:
        this.contactInfoStore.initErrors(errors)
        break
      case RcsWizardSteps.useCase:
        this.useCaseStore.initErrors(errors)
        break
      case RcsWizardSteps.optInMessageInfo:
        this.optInMessageInfoStore.initErrors(errors)
        break
    }
  }

  private _applySubmitErrors = (errors: INormalizedApiError) => {
    this.senderStore.initErrors(errors)
    this.businessInfoStore.initErrors(errors)
    this.contactInfoStore.initErrors(errors)
    this.useCaseStore.initErrors(errors)
    this.optInMessageInfoStore.initErrors(errors)
  }

  loadMessageTypes = async () => {
    if (this._messageTypeOptions.length) return

    runInAction(() => {
      this._messageTypesLoading = true
    })

    try {
      const { data } = await RcsApi.getMessageTypes()

      runInAction(() => {
        this._messageTypeOptions = apiOptionsToDropdownItems(
          data.map((option) => ({ ...option, id: option.value }))
        )
      })
    } catch (error) {
      logger.error(error)
    } finally {
      runInAction(() => {
        this._messageTypesLoading = false
      })
    }
  }

  loadBusinessReferences = async () => {
    if (this._companyTypeOptions.length && this._businessIndustryOptions.length) return

    runInAction(() => {
      this._businessReferencesLoading = true
    })

    try {
      const [companyTypes, businessIndustries] = await Promise.all([
        RcsApi.getCompanyTypes(),
        RcsApi.getBusinessIndustries(),
      ])

      runInAction(() => {
        this._companyTypeOptions = apiOptionsToDropdownItems(
          companyTypes.data.map((option) => ({ ...option, id: option.value }))
        )
        this._businessIndustryOptions = apiOptionsToDropdownItems(
          businessIndustries.data.map((option) => ({ ...option, id: option.value }))
        )
      })
    } catch (error) {
      logger.error(error)
    } finally {
      runInAction(() => {
        this._businessReferencesLoading = false
      })
    }
  }

  loadUseCases = async () => {
    if (this._useCaseOptions.length) return

    runInAction(() => {
      this._useCasesLoading = true
    })

    try {
      const { data } = await RcsApi.getUseCases()

      runInAction(() => {
        this._useCaseOptions = apiOptionsToDropdownItems(
          data.map((option) => ({ ...option, id: option.value }))
        )
      })
    } catch (error) {
      logger.error(error)
    } finally {
      runInAction(() => {
        this._useCasesLoading = false
      })
    }
  }

  init = async () => {
    this.loadMessageTypes()

    try {
      const { data } = await RcsApi.getRcsCompliance()

      if (!data?.id) return

      runInAction(() => {
        this._applyComplianceMeta(data)
      })

      this._hydrate(data)
    } catch (error) {
      if (!(error instanceof AxiosError) || error.response?.status !== 404) {
        logger.error(error)
      }
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  private _applyComplianceMeta = (data: IResponseRcsCompliance) => {
    this._submissionId = data.id ?? null
    this._complianceStatus = data.compliance_status
    this._lastStep = data.step
    this._submission = data
  }

  private _hydrate = (data: IResponseRcsCompliance) => {
    this.senderStore.init(data)
    this.countriesStore.init(data)
    this.businessInfoStore.init(data)
    this.contactInfoStore.init(data)
    this.useCaseStore.init(data)
    this.optInMessageInfoStore.init(data)
  }

  private _showSubmittedForReviewModal = () => {
    modalStore.addModal({
      id: SUBMITTED_MODAL_ID,
      type: ModalTypeList.SUCCESS,
      title: 'Submitted for review',
      desc: 'Fees apply only after your registration is approved and sent for carrier review. Nothing is charged before that happens.',
      showCloseButton: false,
      disabledOnEscClose: true,
      disabledOverlayClose: true,
      primaryAction: {
        text: 'Done',
        fullWidth: true,
        onAction: () => {
          modalStore.removeModal(SUBMITTED_MODAL_ID)
          this.closeWizard()
        },
      },
    })
  }

  openStartOverConfirm = () => {
    modalStore.addModal({
      id: START_OVER_MODAL_ID,
      type: ModalTypeList.WARNING,
      title: 'Start over?',
      desc: "You'll lose your progress. This can't be undone.",
      primaryAction: {
        text: 'Start over',
        onAction: this._startOver,
      },
      secondaryAction: {
        text: 'Cancel',
        onAction: () => modalStore.removeModal(START_OVER_MODAL_ID),
      },
    })
  }

  private _startOver = async () => {
    const id = this._submissionId

    if (id !== null) {
      try {
        await RcsApi.deleteRcsCompliance(id)
      } catch (error) {
        logger.error(error)
        showToast({
          type: 'error',
          title: 'Something went wrong',
          desc: 'Please try again.',
        })
        return
      }
    }

    runInAction(() => {
      this._submissionId = null
      this._complianceStatus = null
      this._lastStep = null
      this._submission = null
      this.senderStore.reset()
      this.countriesStore.reset()
      this.businessInfoStore.reset()
      this.contactInfoStore.reset()
      this.useCaseStore.reset()
      this.optInMessageInfoStore.reset()
      this.reviewStore.reset()
    })

    modalStore.removeModal(START_OVER_MODAL_ID)
    this.navigationStore.goToStep(RcsWizardSteps.senderDetails)
  }

  closeWizard = () => {
    uiStore.changeRoute({ path: RcsPath.root })
  }

  dispose = () => {
    this.senderStore.dispose()
    this.fallbackStore.dispose()
  }
}
